import React, { useRef, useEffect } from 'react';

interface Props {
  view: 'items' | 'history';
  setView: (v: 'items' | 'history') => void;
  search: string;
  setSearch: (s: string) => void;
  filteredCount: number;
  histSearch: string;
  setHistSearch: (s: string) => void;
  histAction: string;
  setHistAction: (s: string) => void;
  histDateFrom: string;
  setHistDateFrom: (s: string) => void;
  histDateTo: string;
  setHistDateTo: (s: string) => void;
  showActionDrop: boolean;
  setShowActionDrop: (v: boolean) => void;
  showMenu: boolean;
  setShowMenu: (v: boolean) => void;
  importBusy: boolean;
  onClearHistory: () => void;
  onDownloadTemplate: () => void;
  onImportClick: () => void;
  onAddClick: () => void;
  onViewItemsReset: () => void;
  onViewHistoryReset: () => void;
}

export function InventoryFilterBar(p: Props) {
  const actionDropRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!p.showActionDrop) return;
    const handler = (e: MouseEvent) => { if (actionDropRef.current && !actionDropRef.current.contains(e.target as Node)) p.setShowActionDrop(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [p.showActionDrop]);

  useEffect(() => {
    if (!p.showMenu) return;
    const handler = (e: MouseEvent) => { if (menuRef.current && !menuRef.current.contains(e.target as Node)) p.setShowMenu(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [p.showMenu]);

  const viewToggleBtn = (target: 'items' | 'history', label: string) => (
    <button
      onClick={() => { p.setView(target); if (target === 'items') p.onViewItemsReset(); else p.onViewHistoryReset(); }}
      style={{
        padding: '7px 22px', fontSize: 14, fontWeight: 600, borderRadius: 8, border: 'none',
        background: p.view === target ? 'var(--accent)' : 'transparent',
        color: p.view === target ? 'white' : 'var(--text2)',
        cursor: 'pointer', fontFamily: 'inherit',
      }}
    >{label}</button>
  );

  const actionOptions = [
    { value: 'all', label: 'All actions' },
    { value: 'ISSUE', label: 'Issued' },
    { value: 'RETURN', label: 'Returned' },
    { value: 'ADD', label: 'Received' },
    { value: 'DELETE', label: 'Deleted' },
  ];
  const currentActionLabel = actionOptions.find(o => o.value === p.histAction)?.label ?? 'All actions';

  return (
    <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius-lg)', padding: '8px 12px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ display: 'flex', gap: 2, padding: 3, background: 'var(--bg)', borderRadius: 10, border: '1px solid var(--border)', flexShrink: 0 }}>
        {viewToggleBtn('items', 'Items')}
        {viewToggleBtn('history', 'History')}
      </div>

      {p.view === 'items' && (<>
        <div className="search-wrap" style={{ maxWidth: 260 }}>
          <svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
          <input className="search-inp" type="text" placeholder="Search items..." value={p.search} onChange={e => p.setSearch(e.target.value)} />
        </div>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 12, color: 'var(--text2)' }}>{p.filteredCount} items</span>
      </>)}

      {p.view === 'history' && (<>
        <div className="search-wrap" style={{ maxWidth: 220 }}>
          <svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
          <input className="search-inp" type="text" placeholder="Search..." value={p.histSearch} onChange={e => p.setHistSearch(e.target.value)} />
        </div>
        <div style={{ position: 'relative' }} ref={actionDropRef}>
          <button onClick={() => p.setShowActionDrop(!p.showActionDrop)} className="filter-sel" style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            {currentActionLabel}
            <svg viewBox="0 0 12 12" style={{ width: 10, height: 10, fill: 'var(--text3)' }}><path d="M2 4l4 4 4-4"/></svg>
          </button>
          {p.showActionDrop && (
            <div className="menu" style={{ position: 'absolute', left: 0, top: '100%', marginTop: 4, minWidth: 160, zIndex: 9999 }}>
              {actionOptions.map(opt => (
                <button
                  key={opt.value}
                  className={`menu-item ${p.histAction === opt.value ? 'is-active' : ''}`}
                  onClick={() => { p.setHistAction(opt.value); p.setShowActionDrop(false); }}
                >
                  {p.histAction === opt.value
                    ? <svg viewBox="0 0 12 12" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 6l3 3 5-5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    : <span style={{ width: 12 }} />}
                  <span className="flex-1 text-truncate">{opt.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div style={{ flex: 1 }} />
        {(p.histDateFrom || p.histDateTo || p.histAction !== 'all' || p.histSearch) && (
          <button
            onClick={p.onClearHistory}
            style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, fontSize: 11, color: 'var(--text2)', cursor: 'pointer', padding: '4px 10px', fontFamily: 'inherit' }}
          >Clear</button>
        )}
      </>)}

      <div style={{ position: 'relative' }} ref={menuRef}>
        <button
          onClick={() => p.setShowMenu(!p.showMenu)}
          style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 10, padding: '8px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          title="Download template / Import CSV"
        >
          <svg viewBox="0 0 16 16" style={{ width: 14, height: 14, fill: 'var(--text3)' }}><circle cx="8" cy="3.5" r="1.2"/><circle cx="8" cy="8" r="1.2"/><circle cx="8" cy="12.5" r="1.2"/></svg>
        </button>
        {p.showMenu && (
          <div className="menu" style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, minWidth: 220, zIndex: 9999 }}>
            <button className="menu-item" onClick={() => { p.onDownloadTemplate(); p.setShowMenu(false); }}>
              <div style={{ width: 28, height: 28, background: 'var(--blue-bg)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg viewBox="0 0 16 16" style={{ width: 14, height: 14, fill: 'none', stroke: 'var(--blue)', strokeWidth: 1.5 }}><path d="M8 2v8M5 7l3 3 3-3"/><path d="M2 12h12"/></svg>
              </div>
              <div className="flex-1">
                <div style={{ fontWeight: 500 }}>Download template</div>
                <div className="text-xs text-subtle" style={{ marginTop: 1 }}>CSV format for bulk import</div>
              </div>
            </button>
            <div className="menu-sep" />
            <button className="menu-item" onClick={() => { p.onImportClick(); p.setShowMenu(false); }} disabled={p.importBusy}>
              <div style={{ width: 28, height: 28, background: 'var(--accent-bg)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg viewBox="0 0 16 16" style={{ width: 14, height: 14, fill: 'none', stroke: 'var(--accent)', strokeWidth: 1.5 }}><path d="M8 10V2M5 5l3-3 3 3"/><path d="M2 12h12"/></svg>
              </div>
              <div className="flex-1">
                <div style={{ fontWeight: 500 }}>{p.importBusy ? 'Importing…' : 'Import from CSV'}</div>
                <div className="text-xs text-subtle" style={{ marginTop: 1 }}>Upload items from a file</div>
              </div>
            </button>
          </div>
        )}
      </div>

      {p.view === 'items' && (
        <button className="hbtn acc" onClick={p.onAddClick} style={{ borderRadius: 8, padding: '7px 16px', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
          <svg viewBox="0 0 16 16" style={{ width: 12, height: 12, fill: 'none', stroke: 'white', strokeWidth: 2 }}><path d="M8 3v10M3 8h10"/></svg>
          Add item
        </button>
      )}
    </div>
  );
}
